import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import updateVariantSelection from "@SupplyLand/Handlers/SupplyLandUpdateVariantSelection";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let productPath;
    if (location.pathname.includes("/Product/")) {
        productPath = location.pathname;
    }
    let productState;
    if (productPath && productPath.length > 0) {
        productState = getProductStateByPath(state, productPath);
    }
    return {
        productPath,
        productState,
    };
};

const mapDispatchToProps = {
    updateVariantSelection,
};

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 10px;
    display: block;
`;

export const BackNavArrow = styled(Typography as any).attrs({ children: "<" })`
    margin: 0 10px;
    display: block;
`;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(
    ({ links, mergeCss, productState, productPath, updateVariantSelection, ...otherProps }) => {
        const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
        const typographyProps = spreadProps("typographyProps");
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
        const history = useHistory();
        const onLinkClickNavigateToUrl = (url: string) => {
            if (productState.value.isVariantParent) {
                for (let i = 0; i < productState.value.variantTraits.length; i++) {
                    const variantTraitId = productState.value.variantTraits[i].id;
                    const traitValueId = "";
                    const productId = productState.value.id;
                    updateVariantSelection({ traitValueId, variantTraitId, productId });
                }
            }
            history.push(url);
        };

        // return null if `links` is an empty empty object, collection, map, or set.
        if (!links || links.length === 0) {
            return null;
        }

        const children = links.map((link: any, index: number) => {
            const children = link.children;

            // yield null if no children
            if (!React.Children.count(children)) {
                return null;
            }
            const key = typeof children === "string" ? `${children}_${index}` : index;
            // matches on:
            //      multiple Links exist in the List;
            //      current Link is the last Link in the List;
            //      current Link contains no HREF value;
            if (links.length > 1 && index === links.length - 1 && !link.href) {
                return (
                    <Typography
                        mergeCss={resolvedMergeCss}
                        {...typographyProps}
                        aria-current="page"
                        key={key}
                        data-test-selector={`breadCrumb_${children}`}
                    >
                        {children}
                    </Typography>
                );
            }
            // for preventing category pages from displaying their own link.
            // matches on:
            //      multiple Links exist in the List;
            //      current Link is the last Link in the List;
            //      current Link contains an HREF value;
            //      productState is undefined;
            if (links.length > 1 && index === links.length - 1 && link.href && productState === undefined) {
                return (
                    <Typography mergeCss={resolvedMergeCss} {...typographyProps} aria-current="page" key={key}>
                        {children}
                    </Typography>
                );
            }
            // for mobile device breadcrumbs.
            // matches on:
            //      one Link exists in the List;
            if (links.length === 1 && index === links.length - 1) {
                return (
                    <React.Fragment key={key}>
                        <BackNavArrow {...typographyProps} />
                        <Link typographyProps={typographyProps} {...link} />
                    </React.Fragment>
                );
            }
            // for displaying link on GrandParent Product pages.
            // matches on:
            //      multiple Links exist in the List;
            //      current Link is the last Link in the List;
            //      current Link contains an HREF value;
            //      productState is populated with the GrandParent Product information;
            if (links.length > 1 && index === links.length - 1 && link.href && productState) {
                return (
                    <React.Fragment key={key}>
                        <Link
                            onClick={() => onLinkClickNavigateToUrl(link.href)}
                            typographyProps={typographyProps}
                            {...link}
                        />
                    </React.Fragment>
                );
            }

            return (
                <React.Fragment key={key}>
                    <Link typographyProps={typographyProps} {...link} />
                    <Slash {...typographyProps} />
                </React.Fragment>
            );
        });
        // return null if `links` map yields an array of nulls
        if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
            return null;
        }

        return (
            <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
                {children}
            </StyledNav>
        );
    },
);

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default connect(mapStateToProps, mapDispatchToProps)(Breadcrumbs);
